import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { getShippingChargesByCountry } from '../../util/apiUtils';
import { isCountryUk, isFullyAccessioned } from '../../util/gearOrderUtil';

const ShippingMethodSelectionUK = forwardRef((props, ref) => {

    const [outboundShippingMethods, setOutboundShippingMethods] = useState(null);
    const [inboundShippingMethods, setInboundShippingMethods] = useState(null);
    const [allOutboundShippingMethods, setAllOutboundShippingMethods] = useState(null);
    const [allInboundShippingMethods, setAllInboundShippingMethods] = useState(null);
    const [loading, setLoading] = useState(false);
    const showDefaultOutboundShippingNote = true;
    const showDefaultInboundShippingNote = true;
    const [showOutboundOverseasCourierNote, setShowOutboundOverseasCourierNote] = useState(false);
    const [showInboundUKPostalShippingNote, setShowInboundUKPostalShippingNote] = useState(false);
    const [outboundShippingMethod, setOutboundShippingMethod] = useState(props?.shippingMethod || null);
    const [inboundShippingMethod, setInboundShippingMethod] = useState(props?.inboundShippingMethod || null);
    const [shipToCountry, setShipToCountry] = useState(
        props?.gearOrder?.patientInfo?.addresses?.find(addr => addr.type === 'shipping')?.country ||
        props?.gearOrder?.practitioner?.office?.address?.country ||
        'Great Britain and Northern Ireland'
    );
    const euroSpecificOutboundShippingMethods = [`Royal Mail Tracked (2-3 day delivery)`, 'Overseas courier (usually received in 2 working days)'];
    const euroSpecificInboundShippingMethods = ['Overseas courier (this is the only option if you are based outside of the UK)'];
    const ukOutboundDefaultMethod = `Royal Mail Tracked (2-3 day delivery)`;
    const ukInboundDefaultMethod = `Genova's recommended overnight courier`;
    const euroOutboundDefaultMethod = 'Overseas courier (usually received in 2 working days)';
    const euroInboundDefaultMethod = 'Overseas courier (this is the only option if you are based outside of the UK)';
    const userType = props?.userInfo?.type || null;
    const onUkShippingMethodChange = props.onUkShippingMethodChange || null;    
    let hasValidReqNumber = props.gearOrder?.requisitions?.some(req => req.reqNumber && req.reqNumber > 0);
    const patientActivation = userType === 'prc' && hasValidReqNumber;
    let hasPaymentProvided = props.gearOrder?.allPayments?.some(payment => payment.fullPaymentAmount && payment.fullPaymentAmount > 0);
    
    /***
     * gearOrder.shippingMethod will save outbound shipping method name saved as in GearShippingCharges. KO will map sent values as follows to save in orderforms.
     * Royal Mail Tracked (2-3 day delivery) = RM 48hr Tracked, Royal Mail Special Delivery = RM NDD 1pm, Overseas courier (usually received in 2 working days) = Courier - Overseas
     */

    useEffect(() => {
        getShippingMethods();
    }, []);

    function getShippingMethods() {
        setLoading(true);
        getShippingChargesByCountry('UK')
            .then((shippingMethods) => {
                if (shippingMethods) {
                    let outboundMethods = shippingMethods
                        .filter(m => m.shippingMethod.startsWith('Outbound:'))
                        .map(m => ({
                            ...m,
                            shippingMethod: m.shippingMethod.replace(/^Outbound:\s*/, '')
                        }));
                    setOutboundShippingMethods(outboundMethods);
                    setAllOutboundShippingMethods(outboundMethods);

                    let inboundMethods = shippingMethods
                        .filter(m => m.shippingMethod.startsWith('Inbound:'))
                        .map(m => ({
                            ...m,
                            shippingMethod: m.shippingMethod.replace(/^Inbound:\s*/, '')
                        }));
                    setInboundShippingMethods(inboundMethods);
                    setAllInboundShippingMethods(inboundMethods);
                }
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        if (outboundShippingMethods && !patientActivation) {
            const shipToUK = shipToCountry && isCountryUk(shipToCountry);
            let filteredMethods = outboundShippingMethods;

            if (!shipToUK && !outboundShippingMethods.every(method => euroSpecificOutboundShippingMethods.includes(method.shippingMethod))) {
                filteredMethods = outboundShippingMethods.filter(method =>
                    euroSpecificOutboundShippingMethods.includes(method.shippingMethod)
                );
            } else if (shipToUK && outboundShippingMethods.some(method => method.shippingMethod.toLowerCase().includes('overseas courier'))) {
                filteredMethods = outboundShippingMethods.filter(method =>
                    !method.shippingMethod.toLowerCase().includes('overseas courier')
                );
            }
            if (!outboundShippingMethod) {
                const defaultMethod = shipToUK ? ukOutboundDefaultMethod : euroOutboundDefaultMethod;
                setOutboundShippingMethod(defaultMethod);
                onUkShippingMethodChange('Outbound', defaultMethod);
            }

            setOutboundShippingMethods(filteredMethods);
        }
        // eslint-disable-next-line
    }, [
        outboundShippingMethods,
        shipToCountry,
        euroSpecificOutboundShippingMethods,
        outboundShippingMethod,
        ukOutboundDefaultMethod,
        euroOutboundDefaultMethod,
        onUkShippingMethodChange
    ]);

    useEffect(() => {
        if (userType !== 'prc' || !inboundShippingMethods) return;

        let filteredInboundMethods = inboundShippingMethods;
        const isNonUk = shipToCountry && !isCountryUk(shipToCountry);
        const allEuroSpecific = inboundShippingMethods.every(method =>
            euroSpecificInboundShippingMethods.includes(method.shippingMethod)
        );

        if (isNonUk && !allEuroSpecific) {
            filteredInboundMethods = inboundShippingMethods.filter(method =>
                euroSpecificInboundShippingMethods.includes(method.shippingMethod)
            );
        }
        if (!inboundShippingMethod && !patientActivation) {
            const defaultInboundMethod = isNonUk ? euroInboundDefaultMethod : ukInboundDefaultMethod;
            setInboundShippingMethod(defaultInboundMethod);
            onUkShippingMethodChange('Inbound', defaultInboundMethod);
        }

        setInboundShippingMethods(filteredInboundMethods);
        // eslint-disable-next-line
    }, [
        userType,
        inboundShippingMethods,
        shipToCountry,
        euroSpecificInboundShippingMethods,
        euroInboundDefaultMethod,
        ukInboundDefaultMethod,
        inboundShippingMethod,
        patientActivation,
        onUkShippingMethodChange
    ]);

    useImperativeHandle(ref, () => ({
        updateShippingMethods(country) {
            setShipToCountry(country);
            if (allOutboundShippingMethods && allInboundShippingMethods) {
                setOutboundShippingMethods(allOutboundShippingMethods);
                setInboundShippingMethods(allInboundShippingMethods);
                setInboundShippingMethod((props?.inboundShippingMethod) || null);
                setOutboundShippingMethod(props?.shippingMethod || null);
            } else {
                getShippingMethods();
            }
        }
    }));

    const handleOutboundShippingChange = (event) => {
        const selectedOutboundShippingMethod = event.target.value;
        setOutboundShippingMethod(selectedOutboundShippingMethod);
        setShowOutboundOverseasCourierNote(false);
        if (selectedOutboundShippingMethod && selectedOutboundShippingMethod.toLowerCase().includes('overseas courier')) {
            setShowOutboundOverseasCourierNote(true);
        }
        if (props.onUkShippingMethodChange) {
            props.onUkShippingMethodChange('Outbound', selectedOutboundShippingMethod);
        }
    };

    const handleInboundShippingChange = (event) => {
        const selectedInboundShippingMethod = event.target.value;
        setInboundShippingMethod(selectedInboundShippingMethod);
        setShowInboundUKPostalShippingNote(false);
        if (selectedInboundShippingMethod && selectedInboundShippingMethod.toLowerCase().includes('uk postal service')) {
            setShowInboundUKPostalShippingNote(true);
        }
        if (props.onUkShippingMethodChange) {
            props.onUkShippingMethodChange('Inbound', selectedInboundShippingMethod);
        }
    };

    return (
        <div>
            {loading && <div>Loading shipping methods...</div>}
            <h4>Delivery Options</h4>
            <div className="separator-2"></div>
            {outboundShippingMethods && outboundShippingMethods.length > 0 && !patientActivation ? (
                <>
                    {showDefaultOutboundShippingNote && (
                        <div style={{ color: '#df4111ff', marginBottom: '13px' }}>
                            <strong>Please Note:</strong> Orders received by 13:00 on a working day will usually be despatched the same day.
                        </div>
                    )}
                    {outboundShippingMethods.map((method, idx) => (
                        <label key={`outbound-${idx}`} style={{ display: 'block', marginBottom: 8 }}>
                            <input
                                id={`formShippingMethod-${idx}`}
                                type="radio"
                                name="outboundShippingMethodUK"
                                value={method.shippingMethod}
                                checked={outboundShippingMethod === method.shippingMethod}
                                disabled={props.isDisabledInputs || patientActivation || isFullyAccessioned(props.gearOrder)}
                                onChange={handleOutboundShippingChange} />
                            {' '}
                            {method.shippingMethod} - £{method.price.toFixed(2)}                            
                        </label>
                    ))}
                </>
            ) : (
                <div>No outbound delivery options available.</div>
            )}
            {showOutboundOverseasCourierNote &&
                <div style={{ background: '#fff3cd', color: '#856404', padding: '12px', borderRadius: '4px', border: '1px solid #ffeeba', marginTop: '16px' }}>
                    <strong>Important:</strong> Shipping time can vary based on location, local customs and available local delivery services.
                </div>
            }
            <br />
            {props.userInfo.type === 'prc' && (
                <>
                <h4>Return Delivery Options</h4> {!inboundShippingMethod && (
                            <div style={{ color: 'red', marginTop: '8px' }}>
                                *Please select a return delivery option.
                            </div>
                        )}
                <div className="separator-2"></div>
                {inboundShippingMethods && inboundShippingMethods.length > 0 ? (
                    <>
                        {showDefaultInboundShippingNote && (
                            <div style={{ color: '#df4111ff', marginBottom: '13px' }}>
                                <strong>Please Note:</strong> We are not open at weekends and so you should not send samples back to us on a Friday.
                            </div>
                        )}
                        {inboundShippingMethods.map((method, idx) => (
                            <label key={`inbound-${idx}`} style={{ display: 'block', marginBottom: 8 }}>
                                <input
                                    id={`formShippingMethod-${idx}`}
                                    type="radio"
                                    name="inboundShippingMethodUK"
                                    value={method.shippingMethod}
                                    checked={inboundShippingMethod === method.shippingMethod}
                                    disabled={props.isDisabledInputs || isFullyAccessioned(props.gearOrder) || hasPaymentProvided}
                                    onChange={handleInboundShippingChange} />
                                {' '}
                                {method.shippingMethod} - £{method.price.toFixed(2)}
                            </label>
                        ))}
                    </>
                ) : (
                    <div>No inbound delivery options available.</div>
                )}
                {showInboundUKPostalShippingNote &&
                    <div style={{ background: '#fff3cd', color: '#856404', padding: '12px', borderRadius: '4px', border: '1px solid #ffeeba', marginTop: '16px' }}>
                        <strong>Important:</strong> Please send any samples back Monday to Thursday on a next day delivery guaranteed before 1pm. Failure to pay for postage in full can delay your samples arriving and they may be discarded for stability reasons. If you select this option and use the recommended courier instead, we will invoice you for the full value of the shipping charge missed.
                    </div>
                }
                </>
         )}
            <br />

        </div>
    );
});


export default ShippingMethodSelectionUK;